import { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { api } from "../api";

/** Resize an image File to a max side of `maxSide` and return a JPEG dataURL. */
async function resizeToDataUrl(file, maxSide = 300, quality = 0.75) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Circular thumbnail that opens a file picker on click. Uploads via PATCH.
 * Props:
 *   - ingredient: { id, image, name }
 *   - onUpdated: (ingredient) => void  — called after successful upload/clear
 *   - size: number  — pixel size (default 40)
 */
export default function ThumbUploader({ ingredient, onUpdated, size = 40 }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const img = ingredient.image || "";

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting the same file works
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    setBusy(true);
    try {
      const dataUrl = await resizeToDataUrl(file, 300, 0.75);
      const { data } = await api.patch(`/mgmt/ingredients/${ingredient.id}`, { image: dataUrl });
      onUpdated?.(data);
    } catch (err) {
      // fall through — parent can refetch
      console.warn("Image upload failed", err);
    } finally { setBusy(false); }
  };

  const clear = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.patch(`/mgmt/ingredients/${ingredient.id}`, { image: "" });
      onUpdated?.(data);
    } finally { setBusy(false); }
  };

  const style = { width: size, height: size };
  return (
    <div className="relative inline-block group" data-testid={`thumb-${ingredient.id}`}>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        aria-label={img ? "Replace photo" : "Upload photo"}
        style={style}
        className={`relative overflow-hidden rounded-full border border-ink/15 bg-cream flex items-center justify-center transition-all hover:border-matcha-dark ${busy ? "opacity-50" : ""}`}
      >
        {img ? (
          <img src={img} alt="" className="w-full h-full object-cover" />
        ) : (
          <Camera size={Math.max(12, Math.round(size * 0.4))} className="text-ink-soft group-hover:text-matcha-dark" />
        )}
        {busy && <div className="absolute inset-0 bg-ink/40 flex items-center justify-center text-[9px] text-bone">…</div>}
      </button>
      {img && (
        <button
          type="button"
          onClick={clear}
          aria-label="Remove photo"
          data-testid={`thumb-clear-${ingredient.id}`}
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-ink text-bone hidden group-hover:flex items-center justify-center hover:bg-red-700 transition-colors"
        >
          <X size={10} />
        </button>
      )}
      <input
        ref={fileRef}
        data-testid={`thumb-input-${ingredient.id}`}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={upload}
        className="hidden"
      />
    </div>
  );
}
